/**
 * @typedef {string} BatchGeoCustomExtentsMode
 **/

(function (window) {
  /**
   * BatchGeoCustomExtents provides a way for users to save custom bounds to
   * their map. BatchGeo by default will fit all markers into the view.
   * However, it is sometimes useful to be able to focus it at a different
   * zoom or position. For example, if you have 100 markers and all are in LA
   * and one is in NY, you may just want to focus on LA.
   *
   * You will be able to access the bounds via the store.
   *
   * @example
   * var customExtents = new BatchGeoCustomExtents({map: map, container: el})
   *
   * // Later... maybe before you save
   * window.per.custom_extent = BatchGeoStore.getState().CustomExtents.bounds
   *
   * // Then when you load the map you could fit the custom bounds:
   * map.fitBounds(BatchGeoStore.getState().CustomExtents.bounds)
   *
   * @param {object} options The options hash
   * @param {jQuery|HTMLElement|string} options.container The selector (or
   * HTML element or jQuery object) of the element you want to inject the
   * button that saves and resets the custom extents.
   * @param {google.maps.Map} options.map Google Maps Map instance that is
   * used to attach the event listeners to.
   * @param {BatchGeoCustomExtentsMode} options.defaultMode The default mode
   * you want to render the button into. For example, you may want to start at
   * RESET if you're on an edit view. It is highly recommended you use the
   * "enums" which are onBatchGeoCustomExtents.modes. It provides OFF, RESET,
   * and SET_CUSTOM
   */
  var BatchGeoCustomExtents = function (options) {
    this.settings = $.extend(
      true,
      {
        container: null,
        map: null,
        defaultMode: BatchGeoCustomExtents.modes.OFF,
      },
      options,
    );

    this._createButton();
    this._attachListeners();
    this.setMode(this.settings.defaultMode);
  };

  /**
   * Enum for custom extent modes which are OFF, RESET, and SET_CUSTOM.
   * @readonly
   * @enum {BatchGeoCustomExtentsMode}
   */
  BatchGeoCustomExtents.modes = {
    /** If this is used the button will fit markers within visible bounds */
    RESET: "RESET",
    /** If this us used the button will be hidden */
    OFF: "OFF",
    /** If this is used it will save the current map bounds when clicked */
    SET_CUSTOM: "SET_CUSTOM",
  };

  /**
   * Sets a mode for the button manually. This is useful if you wanted to
   * override the default state (such as "OFF") in specific views like Edit.
   * For example, on an edit screen, if a user had set a custom extent, it may
   * make sense to automatically enable the RESET mode so they can undo that
   * setting when they created the map.
   *
   * Modes:
   * - OFF: the button will be hidden to the user.
   * - RESET: when clicked, the button will use the default BatchGeo bounding
   * logic which is to fit all markers within the view.
   * - SET_CUSTOM: when clicked, the button will stash the current map bounds
   * and store them in the state.
   *
   * @param {BatchGeoCustomExtentsMode} mode Using an enum from
   * BatchGeoCustomExtents.mode (OFF, RESET, SET_CUSTOM), it will put the
   * button into that state.
   */
  BatchGeoCustomExtents.prototype.setMode = function (mode) {
    var self = this;
    switch (mode) {
      case BatchGeoCustomExtents.modes.RESET:
        this._updateButton("Reset Map Position", function () {
          self._resetMapPositionHandler();
        });
        this._toggleButton(true);
        break;
      case BatchGeoCustomExtents.modes.SET_CUSTOM:
        this._updateButton("Set Default Map Position", function () {
          self._saveMapPositionHandler();
        });
        this._toggleButton(true);
        break;
      case BatchGeoCustomExtents.modes.OFF:
        this._toggleButton(false);
        break;
    }
  };

  /**
   * Creates the button and appends it into the container. It hides it
   * automatically and does not add any text. It just creates a skeleton.
   *
   * @returns {jQuery} The button as a jQuery object
   * @private
   */
  BatchGeoCustomExtents.prototype._createButton = function () {
    if (this._$button) return this._$button;

    return (this._$button = $("<button />", {
      addClass: "batchgeo-custom-extent",
      type: "button",
      toggle: false,
    }).appendTo(this.settings.container));
  };

  /**
   * Updates the buttons text and click handler. It also removes all events on
   * the button before adding the new click handler.
   *
   * @param {string} text The text to put placed into the button
   * @param {function} handler The function to call when the button is clicked
   * @private
   */
  BatchGeoCustomExtents.prototype._updateButton = function (text, handler) {
    this._$button
      .text(text)
      // remove all events
      .off()
      // then add new event
      .on("click", handler);
  };

  /**
   * Method to show/hide the button.
   *
   * @param {boolean} visibility If true it shows the button and if false it
   * hides the button.
   * @private
   */
  BatchGeoCustomExtents.prototype._toggleButton = function (visibility) {
    this._$button.toggle(visibility);
  };

  /**
   * The handler for when the button is clicked while in SET_CUSTOM mode. It
   * will dispatch a BatchGeoStore event (SET_EXTENT) with the new bounds when
   * clicked and it will automatically set the map mode to RESET as well.
   * @private
   */
  BatchGeoCustomExtents.prototype._saveMapPositionHandler = function () {
    BatchGeoStore.dispatch({
      type: "SET_EXTENT",
      bounds: this.settings.map.getBounds().toJSON(),
    });

    this.setMode(BatchGeoCustomExtents.modes.RESET);
  };

  /**
   * The handler will force the map back into default BatchGeo bounding box
   * state which is to fit all markers in the window, hide the button and will
   * set the custom extent to null (through the store's reducer).
   * @private
   */
  BatchGeoCustomExtents.prototype._resetMapPositionHandler = function () {
    window.batchGeoGoogleMapsMarker.zoomToMarkers();
    BatchGeoStore.dispatch({ type: "DELETE_EXTENT" });
    this._toggleButton(false);
  };

  /**
   * Sets the initial event listeners for the component which include a
   * dragend event on the map. When the map is dragged and let go the
   * SET_CUSTOM map mode is run.
   * @private
   */
  BatchGeoCustomExtents.prototype._attachListeners = function () {
    var self = this;
    this.settings.map.addListener("dragend", function (_event) {
      self.setMode(BatchGeoCustomExtents.modes.SET_CUSTOM);
    });
  };

  window.BatchGeoCustomExtents = BatchGeoCustomExtents;
})(window);
